import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Building2, CheckCircle, Clock, DollarSign } from 'lucide-react';

export function InventoryOverview() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['inventory-overview', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase.from('developer_profiles').select('id').eq('user_id', user!.id).maybeSingle();
      const { data: myAgency } = await supabase.from('agencies').select('id').eq('created_by', user!.id).maybeSingle();

      const orParts: string[] = [];
      if (devProfile?.id) orParts.push(`developer_id.eq.${devProfile.id}`);
      if (myAgency?.id) orParts.push(`marketing_agency_id.eq.${myAgency.id}`);
      if (orParts.length === 0) return { projects: [], totals: { units: 0, available: 0, booked: 0, sold: 0, revenue: 0 } };

      const { data: projects } = await supabase
        .from('projects')
        .select('id, name, city, total_units, available_units, status')
        .or(orParts.join(','));

      const projectIds = (projects || []).map(p => p.id);
      let bookingsData: any[] = [];
      if (projectIds.length > 0) {
        const { data: bk } = await supabase
          .from('bookings')
          .select('project_id, booking_status, booking_amount')
          .in('project_id', projectIds);
        bookingsData = bk || [];
      }

      const projectStats = (projects || []).map(p => {
        const pBookings = bookingsData.filter(b => b.project_id === p.id);
        const booked = pBookings.filter(b => b.booking_status === 'booked' || b.booking_status === 'confirmed').length;
        const sold = pBookings.filter(b => b.booking_status === 'transferred').length;
        const revenue = pBookings
          .filter(b => b.booking_status !== 'cancelled')
          .reduce((sum, b) => sum + (Number(b.booking_amount) || 0), 0);
        return {
          ...p,
          booked,
          sold,
          revenue,
          available: (p.total_units || 0) - booked - sold,
        };
      });

      const totals = {
        units: projectStats.reduce((s, p) => s + (p.total_units || 0), 0),
        available: projectStats.reduce((s, p) => s + p.available, 0),
        booked: projectStats.reduce((s, p) => s + p.booked, 0),
        sold: projectStats.reduce((s, p) => s + p.sold, 0),
        revenue: projectStats.reduce((s, p) => s + p.revenue, 0),
      };

      return { projects: projectStats, totals };
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Inventory Overview</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const { projects: projectStats, totals } = data || { projects: [], totals: { units: 0, available: 0, booked: 0, sold: 0, revenue: 0 } };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" /> Inventory Overview
        </h2>
        <p className="text-sm text-muted-foreground">Summary of units and bookings across all projects.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Units', value: totals.units, icon: Building2, color: 'text-primary bg-primary/10' },
          { label: 'Available', value: totals.available, icon: CheckCircle, color: 'text-green-600 bg-green-100' },
          { label: 'Booked', value: totals.booked, icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
          { label: 'Sold', value: totals.sold, icon: Package, color: 'text-blue-600 bg-blue-100' },
          { label: 'Total Revenue', value: `${totals.revenue.toLocaleString()} ETB`, icon: DollarSign, color: 'text-emerald-600 bg-emerald-100' },
        ].map(item => (
          <Card key={item.label} className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl ${item.color} flex items-center justify-center shrink-0`}>
                <item.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-lg font-bold">{item.value}</div>
                <div className="text-xs text-muted-foreground">{item.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-project breakdown */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">Project Breakdown</h3>
        {projectStats.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No projects yet.</p>
        ) : (
          <div className="grid gap-4">
            {projectStats.map((p: any) => {
              const total = p.total_units || 1;
              const sellThrough = Math.round(((p.booked + p.sold) / total) * 100);
              return (
                <Card key={p.id} className="rounded-2xl border-0 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="font-semibold text-sm">
                          {typeof p.name === 'object' ? (p.name as any)?.en : p.name}
                        </div>
                        <div className="text-xs text-muted-foreground">{p.city} · {p.status?.replace('_', ' ')}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{p.revenue.toLocaleString()} ETB</div>
                        <div className="text-[10px] text-muted-foreground">Booking Revenue</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 text-center text-xs mb-3">
                      <div><div className="font-bold text-sm">{total}</div><span className="text-muted-foreground">Total</span></div>
                      <div><div className="font-bold text-sm text-green-600">{p.available}</div><span className="text-muted-foreground">Available</span></div>
                      <div><div className="font-bold text-sm text-yellow-600">{p.booked}</div><span className="text-muted-foreground">Booked</span></div>
                      <div><div className="font-bold text-sm text-blue-600">{p.sold}</div><span className="text-muted-foreground">Sold</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={sellThrough} className="flex-1 h-2" />
                      <span className="text-xs font-medium text-muted-foreground">{sellThrough}% sold</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
